// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useCallback, useEffect, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Code, AlertTriangle, Plus, Move } from "lucide-react"
import { useSchemaState } from "@/hooks/schema/useSchemaState"
import { useSchemaValidation } from "@/hooks/schema/useSchemaValidation"
import { SectionEditor } from "./SectionEditor"
import { SchemaPreview } from "./SchemaPreview"
import type { ConfigComponentProps } from '../../ConfigForm'
import { getAvailableSchemaTypes, getSchemaTypeDefinition } from '@/types/schema-registry'
import { getSchemaForType, updateSchemaForType, getDefaultSchemaType } from '@/utils/schema/schema-type-utils'
import { getIconComponent } from '@/utils/icon-utils'

// Simple component to render icons with fallback
const IconRenderer = ({ iconName, className }: { iconName?: string; className?: string }) => {
  const IconComponent = getIconComponent(iconName)
  return IconComponent ? <IconComponent className={className} /> : null
}

export function SchemaEditor({ 
  config, 
  onUpdate, 
  isLoading
}: ConfigComponentProps): JSX.Element {
  // Schema type selection state - initialize with default
  const [selected_schema_type, setSelectedSchemaType] = useState<string>(() => 
    getDefaultSchemaType(config)
  )
  
  // Track if user has made any changes
  const hasUserInteracted = useRef(false)
  const isInitialMount = useRef(true)
  const isExternalUpdate = useRef(false)
  
  // Track the previous config to detect workspace changes
  const prevConfigRef = useRef(config)
  const [isWorkspaceChanging, setIsWorkspaceChanging] = useState(false)
  
  // Reset schema type selection when workspace changes
  useEffect(() => {
    const prevWorkspaceId = prevConfigRef.current?.sk
    const currentWorkspaceId = config?.sk
    
    if (prevWorkspaceId !== currentWorkspaceId && currentWorkspaceId) {
      console.log('[SchemaEditor] Workspace changed, resetting schema type:', { 
        from: prevWorkspaceId, 
        to: currentWorkspaceId 
      })
      
      // Set workspace changing flag
      setIsWorkspaceChanging(true)
      
      // Reset to default schema type for new workspace
      const defaultType = getDefaultSchemaType(config)
      setSelectedSchemaType(defaultType)
      
      // Reset interaction tracking
      hasUserInteracted.current = false
      isInitialMount.current = true
      
      // Clear workspace changing flag after config has settled
      setTimeout(() => {
        setIsWorkspaceChanging(false)
      }, 100)
    }
    
    prevConfigRef.current = config
  }, [config?.sk, config])
  
  // Get current schema for selected type
  const current_schema = getSchemaForType(config, selected_schema_type)
  
  // Track previous schema for external change detection
  const prevSchemaRef = useRef(current_schema)
  
  // Generate a unique key for the schema state to force re-initialization
  // when workspace or schema type changes
  const schema_state_key = `${config?.sk || 'no-workspace'}-${selected_schema_type}`
  
  // Check if we should wait for config to settle after workspace change
  const shouldWaitForConfig = isWorkspaceChanging || (isLoading && !current_schema)
  
  // Use key prop to force re-initialization when workspace or schema type changes
  const {
    generatorState,
    sortedSections,
    generatedSchema,
    handleDialogTitleChange: originalHandleDialogTitleChange,
    handleSectionChange: originalHandleSectionChange,
    addSection: originalAddSection,
    removeSection: originalRemoveSection,
    duplicateSection: originalDuplicateSection,
    addField: originalAddField,
    removeField: originalRemoveField,
    updateField: originalUpdateField,
    moveFieldUp: originalMoveFieldUp,
    moveFieldDown: originalMoveFieldDown,
    moveSectionUp: originalMoveSectionUp,
    moveSectionDown: originalMoveSectionDown
  } = useSchemaState({ 
    currentSchema: shouldWaitForConfig ? undefined : current_schema,
    schema_type: selected_schema_type,
    key: schema_state_key
  })
  
  // Wrap all handlers to track user interaction
  const handleDialogTitleChange = useCallback((title: string) => {
    hasUserInteracted.current = true
    originalHandleDialogTitleChange(title)
  }, [originalHandleDialogTitleChange])
  
  const handleSectionChange = useCallback((uuid: string, updates: any) => {
    hasUserInteracted.current = true
    originalHandleSectionChange(uuid, updates)
  }, [originalHandleSectionChange])
  
  const addSection = useCallback(() => {
    hasUserInteracted.current = true
    originalAddSection()
  }, [originalAddSection])
  
  const removeSection = useCallback((uuid: string) => {
    hasUserInteracted.current = true
    originalRemoveSection(uuid)
  }, [originalRemoveSection])
  
  const duplicateSection = useCallback((uuid: string) => {
    hasUserInteracted.current = true
    originalDuplicateSection(uuid)
  }, [originalDuplicateSection])
  
  const addField = useCallback((sectionUuid: string, fieldType: any) => {
    hasUserInteracted.current = true
    originalAddField(sectionUuid, fieldType)
  }, [originalAddField])
  
  const removeField = useCallback((sectionUuid: string, fieldUuid: string) => {
    hasUserInteracted.current = true
    originalRemoveField(sectionUuid, fieldUuid)
  }, [originalRemoveField])
  
  const updateField = useCallback((sectionUuid: string, fieldUuid: string, updates: any) => {
    hasUserInteracted.current = true
    originalUpdateField(sectionUuid, fieldUuid, updates)
  }, [originalUpdateField])
  
  const moveFieldUp = useCallback((sectionUuid: string, fieldUuid: string) => {
    hasUserInteracted.current = true
    originalMoveFieldUp(sectionUuid, fieldUuid)
  }, [originalMoveFieldUp])
  
  const moveFieldDown = useCallback((sectionUuid: string, fieldUuid: string) => {
    hasUserInteracted.current = true
    originalMoveFieldDown(sectionUuid, fieldUuid)
  }, [originalMoveFieldDown])
  
  const moveSectionUp = useCallback((uuid: string) => {
    hasUserInteracted.current = true
    originalMoveSectionUp(uuid)
  }, [originalMoveSectionUp])
  
  const moveSectionDown = useCallback((uuid: string) => {
    hasUserInteracted.current = true
    originalMoveSectionDown(uuid)
  }, [originalMoveSectionDown])
  
  // Detect external schema changes
  useEffect(() => {
    const currentSchemaStr = JSON.stringify(current_schema)
    const prevSchemaStr = JSON.stringify(prevSchemaRef.current)
    
    if (currentSchemaStr !== prevSchemaStr && !hasUserInteracted.current) {
      console.log('[SchemaEditor] External schema change detected, marking as external update')
      isExternalUpdate.current = true
      setTimeout(() => {
        isExternalUpdate.current = false
      }, 100)
    }
    
    prevSchemaRef.current = current_schema
  }, [current_schema])

  // Propagate schema changes to parent component only after user interaction
  useEffect(() => {
    // Skip the initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    
    // Skip if this is an external update
    if (isExternalUpdate.current) {
      console.log('[SchemaEditor] Skipping propagation due to external update')
      return
    }
    
    if (generatedSchema && current_schema && hasUserInteracted.current) {
      // Only update if the schema has actually changed
      if (JSON.stringify(generatedSchema) !== JSON.stringify(current_schema)) {
        console.log('[SchemaEditor] Propagating schema changes to parent')
        const updates = updateSchemaForType(config, selected_schema_type, generatedSchema)
        onUpdate(updates)
      }
    }
  }, [generatedSchema, current_schema, config, selected_schema_type, onUpdate])
  
  // Reset interaction tracking when schema type changes or workspace changes
  useEffect(() => {
    console.log('[SchemaEditor] Resetting interaction tracking for schema type:', selected_schema_type)
    hasUserInteracted.current = false
    isInitialMount.current = true
  }, [selected_schema_type, config?.sk])

  const validation = useSchemaValidation(generatorState)
  
  // Get available schema types
  const available_schema_types = getAvailableSchemaTypes()
  const current_schema_type_def = getSchemaTypeDefinition(selected_schema_type)
  
  // Handle schema type selection
  const handleSchemaTypeChange = useCallback((new_schema_type: string) => {
    // Check if there are pending changes in the current schema
    if (hasUserInteracted.current && generatedSchema && current_schema && 
        JSON.stringify(generatedSchema) !== JSON.stringify(current_schema)) {
      if (!confirm('You have unsaved changes to the current schema. Do you want to discard them?')) {
        return
      }
    }
    
    console.log('[SchemaEditor] Schema type changed from', selected_schema_type, 'to', new_schema_type)
    setSelectedSchemaType(new_schema_type)
    
    // Reset interaction tracking for new schema type
    hasUserInteracted.current = false
    isInitialMount.current = true
  }, [generatedSchema, current_schema, selected_schema_type])

  // Show loading state during workspace transitions
  if (shouldWaitForConfig) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5 text-blue-500" />
              Loading Schema Configuration...
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading workspace schema...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Schema Type Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IconRenderer iconName={current_schema_type_def?.icon} className="h-5 w-5 text-blue-500" />
            Schema Type
          </CardTitle>
          <CardDescription>
            Select the type of analysis schema to configure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Schema Type</Label>
            <Select value={selected_schema_type} onValueChange={handleSchemaTypeChange} disabled={isLoading}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select schema type" />
              </SelectTrigger>
              <SelectContent>
                {available_schema_types.map((schema_type) => (
                  <SelectItem key={schema_type.id} value={schema_type.id}>
                    <div className="flex items-center gap-2">
                      <IconRenderer iconName={schema_type.icon} className="h-4 w-4" />
                      <div>
                        <div className="font-medium">{schema_type.name}</div>
                        <div className="text-sm text-muted-foreground">{schema_type.description}</div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Validation Errors */}
      {validation.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              <div className="font-medium">Schema validation errors:</div>
              <ul className="list-disc list-inside text-sm space-y-1">
                {validation.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Schema Builder */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-blue-500" />
            Schema Builder - {current_schema_type_def?.name || selected_schema_type}
          </CardTitle>
          <CardDescription className="mt-1">
            Configure the structure of your {current_schema_type_def?.description.toLowerCase() || 'analysis schema'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="builder" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="builder">Builder</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            
            <TabsContent value="builder" className="space-y-4">
              {/* Dialog Title Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Schema Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>Dialog Title</Label>
                    <Input
                      value={generatorState.dialogTitle}
                      onChange={(e) => handleDialogTitleChange(e.target.value)}
                      placeholder="Analysis Results"
                      disabled={isLoading}
                    />
                    <p className="text-sm text-muted-foreground">
                      This title will appear in the AI response and in the UI
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Sections */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Move className="h-5 w-5 text-blue-500" />
                      Analysis Sections ({sortedSections.length})
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSection}
                      disabled={isLoading}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Section
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {sortedSections.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Move className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No analysis sections configured</p>
                      <p className="text-sm">Add sections to define your schema structure</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedSections.map((section, index) => (
                        <SectionEditor
                          key={section.uuid}
                          section={section}
                          schema_type={selected_schema_type}
                          onSectionChange={handleSectionChange}
                          onDuplicate={duplicateSection}
                          onRemove={removeSection}
                          onAddField={addField}
                          onRemoveField={removeField}
                          onUpdateField={updateField}
                          onMoveFieldUp={moveFieldUp}
                          onMoveFieldDown={moveFieldDown}
                          onMoveSectionUp={moveSectionUp}
                          onMoveSectionDown={moveSectionDown}
                          isLoading={isLoading}
                          isFirstSection={index === 0}
                          isLastSection={index === sortedSections.length - 1}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="preview" className="space-y-4">
              <SchemaPreview
                schema={generatedSchema}
                validation={validation}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

    </div>
  )
}
